import { useState, useCallback } from "react";
import { apiClient } from "../../utils/apiClient";

export interface ExecuteCommandResponse {
  success: boolean;
  result?: Record<string, any>;
  error?: string;
  logs?: string[];
}

export interface UseRunAdminCommandResult {
  executeCommand: (
    command: string,
    args: Record<string, any>
  ) => Promise<ExecuteCommandResponse>;
  executeCommandWithFile: (
    command: string,
    args: Record<string, any>,
    file: File,
    fileFieldName?: string
  ) => Promise<ExecuteCommandResponse>;
  isExecuting: boolean;
  result: ExecuteCommandResponse | null;
  clearResult: () => void;
}

export function useRunAdminCommand(): UseRunAdminCommandResult {
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState<ExecuteCommandResponse | null>(null);

  const executeCommand = useCallback(
    async (
      command: string,
      args: Record<string, any>
    ): Promise<ExecuteCommandResponse> => {
      setIsExecuting(true);
      try {
        const response = await apiClient.post<ExecuteCommandResponse>(
          "/admin/commands/execute",
          {
            command,
            arguments: args,
          }
        );
        setResult(response);
        return response;
      } catch (err: any) {
        const errorResponse: ExecuteCommandResponse = {
          success: false,
          error: err.message || "Command execution failed",
        };
        setResult(errorResponse);
        return errorResponse;
      } finally {
        setIsExecuting(false);
      }
    },
    []
  );

  const executeCommandWithFile = useCallback(
    async (
      command: string,
      args: Record<string, any>,
      file: File,
      fileFieldName: string = "file"
    ): Promise<ExecuteCommandResponse> => {
      setIsExecuting(true);
      try {
        const formData = new FormData();
        formData.append("command", command);
        formData.append(fileFieldName, file);
        formData.append("arguments", JSON.stringify(args));

        const response = await apiClient.fetch<ExecuteCommandResponse>(
          "/admin/commands/execute",
          {
            method: "POST",
            body: formData,
          }
        );
        setResult(response);
        return response;
      } catch (err: any) {
        const errorResponse: ExecuteCommandResponse = {
          success: false,
          error: err.message || "Command execution failed",
        };
        setResult(errorResponse);
        return errorResponse;
      } finally {
        setIsExecuting(false);
      }
    },
    []
  );

  const clearResult = useCallback(() => {
    setResult(null);
  }, []);

  return {
    executeCommand,
    executeCommandWithFile,
    isExecuting,
    result,
    clearResult,
  };
}
